import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { EventLevelHeader } from "src/ui/shared/table/headers/EventLevelHeader";

import { EventReport } from "@dashboard/devices/types";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { alternatingBg, ContentContainer } from "@dashboard/shared/styles";
import { Box, Paper, TableContainer, TableRow, useTheme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { MinimalTable, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { ColumnFiltersState, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useGetAllEvents } from "../../hooks/useGetAllEvents";

import { columns, getEventLevel, useStyles } from "./AdminEventsPage.utils";

const AdminEventsPage = () => {
    const [pageIndex, setPageIndex] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(12);
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const theme = useTheme();

    const { data, isLoading } = useGetAllEvents({
        pageIndex: pageIndex + 1,
        pageSize,
        level: getEventLevel(columnFilters),
    });

    const table = useReactTable<EventReport>({
        data: data.list,
        columns,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleResetFilter = () => {
        setColumnFilters([]);
        setPageIndex(0);
    };

    return (
        <PageContainer>
            <PageHeader
                title={t("pages:adminEventsPage.title")}
                iconName="fa-regular fa-bell"
                description={t("pages:adminEventsPage.description")}
            />
            <ContentContainer>
                <Box className={classes.content}>
                    <TableContainer component={Paper}>
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header}>
                                                {header.id === "level" ? (
                                                    <EventLevelHeader
                                                        columnFilters={columnFilters}
                                                        setColumnFilters={setColumnFilters}
                                                    />
                                                ) : null}
                                            </Header>
                                        ))}
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody
                                table={table}
                                filter={{ isActive: !!columnFilters.length, onResetFilter: handleResetFilter }}
                                loading={isLoading}
                                noDataMessage={t("pages:adminEventsPage.noEvents")}
                            >
                                {table.getRowModel().rows.map((row, index) => (
                                    <TableRow key={row.original.id} style={alternatingBg(index, theme)}>
                                        {row.getVisibleCells().map((cell) =>
                                            flexRender(cell.column.columnDef.cell, {
                                                ...cell.getContext(),
                                                key: cell.id,
                                            }),
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </TableContainer>
                    <CustomTablePagination
                        component="div"
                        rowsPerPageOptions={[12, 24, 48]}
                        count={data.count}
                        rowsPerPage={pageSize}
                        page={pageIndex}
                        onPageChange={(_event, page) => setPageIndex(page)}
                        onRowsPerPageChange={(e) => {
                            const size = e.target.value ? Number(e.target.value) : 12;
                            setPageSize(size);
                            setPageIndex(0);
                        }}
                    />
                </Box>
            </ContentContainer>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminEventsPage, {
        title: i18next.t("pages:adminEventsPage.metaTitle"),
        description: i18next.t("pages:adminEventsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
